import React, { useCallback } from 'react';
import {
  Accordion,
  accordionItemBuilder,
  Text,
  TextButton,
  Box,
} from '@wix/design-system';
import { ChevronDown, ChevronUp } from '@wix/wix-ui-icons-common';
import { useTranslation } from '@wix/fe-essentials-standalone';
import { communityNavSubHeaderDataHooks } from '../../CommunityNavSubHeader.dataHooks';
import {
  COMMUNITY_NAV_DROPDOWN_OPTIONS,
  CommunityNavDropdownOption,
  DROPDOWN_IDS,
  DropdownId,
} from '../../navSubHeaderUtils';
import useCommunityNavSubHeaderBi, {
  ACTION_NAMES,
} from '../useCommunityNavSubHeaderBi';
import s from './CommunityNavModalMainScreen.module.scss';

const CommunityNavModalMainScreen = () => {
  const { t } = useTranslation();

  return (
    <Box display="block" margin="0 -24px" className={s.accordionContainer}>
      <Accordion
        dataHook={communityNavSubHeaderDataHooks.mobileContent()}
        horizontalPadding="none"
        skin="light"
        hideShadow
        items={DROPDOWN_IDS.map((id: DropdownId) =>
          accordionItemBuilder({
            title: (
              <Box display="block" paddingLeft="SP4">
                <Text size="medium" weight="thin">
                  {t(`marketplace.community-nav-sub-header.${id}`)}
                </Text>
              </Box>
            ),
            children: (
              <CategoryLinks options={COMMUNITY_NAV_DROPDOWN_OPTIONS[id]} />
            ),
            buttonType: 'node',
            showLabel: 'always',
            collapseLabel: <ChevronUpIcon />,
            expandLabel: <ChevronDownWithBI id={id} />,
          }),
        )}
      />
    </Box>
  );
};

export default CommunityNavModalMainScreen;

const ChevronUpIcon = ({ onClick }: { onClick?: () => void }) => (
  <Box paddingRight="SP4">
    <TextButton onClick={onClick}>
      <ChevronUp />
    </TextButton>
  </Box>
);

interface ChevronDownWithBIProps {
  id: string;
}

const ChevronDownWithBI = ({ id }: ChevronDownWithBIProps) => {
  const { t } = useTranslation();
  const { reportElementsClick } = useCommunityNavSubHeaderBi();

  return (
    <Box paddingRight="SP4">
      <TextButton
        onClick={() =>
          reportElementsClick({
            elementTitle: t(`marketplace.community-nav-sub-header.${id}`),
            actionName: ACTION_NAMES.OpenMenu,
          })
        }
      >
        <ChevronDown />
      </TextButton>
    </Box>
  );
};

const CommunityNavLink = ({
  id,
  labelKey,
  hrefKey,
  linkType,
  clickType,
}: CommunityNavDropdownOption) => {
  const { t } = useTranslation();
  const targetHref = t(hrefKey);
  const { reportSubHeaderClick } = useCommunityNavSubHeaderBi();

  const handleClick = useCallback(() => {
    reportSubHeaderClick(id, {
      elementTitle: t(labelKey),
      actionName:
        linkType === 'external'
          ? ACTION_NAMES.ExternalLink
          : ACTION_NAMES.PageLink,
      value: targetHref,
      clickType: clickType,
    });
  }, [id, labelKey, linkType, targetHref, reportSubHeaderClick, t]);

  return (
    <div role="listitem">
      <Box display="block" padding="SP2 0 SP2 SP8">
        <TextButton
          as="a"
          skin="dark"
          weight="thin"
          size="medium"
          href={targetHref}
          target="_blank"
          rel="noopener noreferrer"
          className={s.categoryLink}
          onClick={handleClick}
        >
          <Box display="block">
            <Text size="medium" weight="thin">
              {t(labelKey)}
            </Text>
          </Box>
        </TextButton>
      </Box>
    </div>
  );
};

interface CategoryLinksProps {
  options: readonly CommunityNavDropdownOption[];
}

const CategoryLinks = ({ options }: CategoryLinksProps) => (
  <Box backgroundColor="#FFF" direction="vertical">
    {options.map((option) => (
      <CommunityNavLink key={option.id} {...option} />
    ))}
  </Box>
);
